import { createRootRouteWithContext, createRoute, Link, Outlet } from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import {
  Bell,
  BookOpen,
  Building2,
  CheckCircle2,
  ChevronRight,
  Clock3,
  Download,
  Eye,
  FileText,
  Film,
  LayoutDashboard,
  Layers,
  Menu,
  MessageSquare,
  MonitorPlay,
  PlayCircle,
  Plus,
  Rocket,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
  UploadCloud,
  Wand2,
  X,
  Zap,
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface RouterContext {
  queryClient: QueryClient
}

type Status = 'Approved' | 'In review' | 'Needs work' | 'Draft' | 'Ready'
type Panel = 'overview' | 'structure' | 'session' | 'audit' | 'exports' | 'settings'

interface SessionItem {
  id: string
  title: string
  duration: string
  status: Status
  progress: number
  source: string
  script: string
  storyboard: string[]
  audit: string[]
}

interface Chapter {
  id: string
  title: string
  status: Status
  sessions: SessionItem[]
}

interface ProductCourse {
  id: string
  title: string
  subtitle: string
  status: Status
  chapters: Chapter[]
}

interface Project {
  id: string
  title: string
  client: string
  status: Status
  products: ProductCourse[]
}

interface DemoResponse {
  title: string
  detail: string
  time: string
}

type StyleKey = 'corporate' | 'cinematic'

const STYLE_OUTPUTS: Record<StyleKey, { label: string; subtitle: string; videoUrl: string; reviewPageUrl?: string }> = {
  corporate: {
    label: 'Corporate (B5)',
    subtitle: 'Clean LMS-friendly cut · ~02:00',
    videoUrl: 'https://iso-video.konzult.in/iso-14001-clause-4-1-client-sample.mp4',
    reviewPageUrl: 'https://iso-video.konzult.in/iso-14001-learning-module.html',
  },
  cinematic: {
    label: 'Cinematic (Dark Neon v3)',
    subtitle: '1920×1080 beat-synced · ~02:06',
    videoUrl: 'https://iso-video.konzult.in/iso-14001-dark-neon-layoutfix-full-review.mp4',
  },
}

const projects: Project[] = [
  {
    id: 'project-nandan-academy',
    title: 'Nandan Training Academy',
    client: 'Training content production',
    status: 'In review',
    products: [
      {
        id: 'course-management-systems',
        title: 'Management Systems Training',
        subtitle: 'Professional compliance learning library',
        status: 'In review',
        chapters: [
          {
            id: 'chapter-context',
            title: 'Chapter 1 · Context of the organization',
            status: 'In review',
            sessions: [
              {
                id: 'session-clause-4-1',
                title: 'Clause 4.1 made practical',
                duration: '02:06',
                status: 'In review',
                progress: 76,
                source: 'Clause notes, audit examples, narration draft · corporate + cinematic cuts ready',
                script:
                  'Explain external and internal issues using a practical factory example. Keep one idea per scene and avoid crowded text.',
                storyboard: ['Opening hook', 'Clause 4.1 reveal', 'Mistake cards', 'Stakeholder map', 'Recap'],
                audit: ['Text size safe', 'Scene spacing safe', 'Layout-fix pass applied', 'Approval pending'],
              },
              {
                id: 'session-interested-parties',
                title: 'Interested parties',
                duration: '03:20',
                status: 'Draft',
                progress: 34,
                source: 'Client notes + training examples',
                script:
                  'Show customers, regulators, employees, suppliers and community as visible stakeholders around the process.',
                storyboard: ['Stakeholder map', 'Needs table', 'Risk example', 'Mini quiz'],
                audit: ['Needs storyboard review', 'Voice not attached', 'Preview not approved'],
              },
            ],
          },
          {
            id: 'chapter-risks',
            title: 'Chapter 2 · Risks, opportunities and controls',
            status: 'Draft',
            sessions: [
              {
                id: 'session-risk-thinking',
                title: 'Risk-based thinking',
                duration: '04:10',
                status: 'Draft',
                progress: 18,
                source: 'Outline only',
                script: 'Turn risk examples into simple decision cards and action steps.',
                storyboard: ['Risk story', 'Control decision', 'Action owner', 'Evidence'],
                audit: ['Source material incomplete', 'Needs voice plan'],
              },
            ],
          },
        ],
      },
      {
        id: 'course-safety-induction',
        title: 'Safety Induction Course',
        subtitle: 'Worker onboarding and plant safety modules',
        status: 'Ready',
        chapters: [
          {
            id: 'chapter-ppe',
            title: 'Chapter 1 · PPE and entry rules',
            status: 'Ready',
            sessions: [
              {
                id: 'session-ppe-basics',
                title: 'PPE basics',
                duration: '02:45',
                status: 'Ready',
                progress: 92,
                source: 'Safety checklist and photos',
                script: 'Make PPE rules visual, short and repeatable for new joiners.',
                storyboard: ['Entry gate', 'PPE checklist', 'Unsafe example', 'Final reminder'],
                audit: ['Approved layout', 'Audio synced', 'Export ready'],
              },
            ],
          },
        ],
      },
    ],
  },
]

const panels: Array<{ id: Panel; label: string; description: string; icon: ReactNode }> = [
  { id: 'overview', label: 'Dashboard', description: 'Today’s work', icon: <LayoutDashboard className="h-4 w-4" /> },
  { id: 'structure', label: 'Project map', description: 'Courses and chapters', icon: <Layers className="h-4 w-4" /> },
  { id: 'session', label: 'Session studio', description: 'Preview and script', icon: <MonitorPlay className="h-4 w-4" /> },
  { id: 'audit', label: 'Quality audit', description: 'Readability gate', icon: <ShieldCheck className="h-4 w-4" /> },
  { id: 'exports', label: 'Export room', description: 'Review and MP4', icon: <Download className="h-4 w-4" /> },
  { id: 'settings', label: 'Settings', description: 'Brand and roles', icon: <Settings className="h-4 w-4" /> },
]

const workflow = [
  { label: 'Create project', detail: 'Client or company workspace is ready', done: true },
  { label: 'Add products/courses', detail: 'Multiple training courses under one project', done: true },
  { label: 'Plan chapters', detail: 'Course broken into clear learning sections', done: true },
  { label: 'Build sessions', detail: 'Storyboard, script and preview prepared', done: true },
  { label: 'Review HTML preview', detail: 'Layout and readability checked before video', done: false },
  { label: 'Approve final export', detail: 'MP4/LMS/social output after quality gate', done: false },
]

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
})

function RootLayout() {
  return <Outlet />
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: StudioPage,
})

const studioRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/studio',
  component: StudioPage,
})

function StudioPage() {
  const project = projects[0]
  const [activePanel, setActivePanel] = useState<Panel>('overview')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [selectedProductId, setSelectedProductId] = useState(project.products[0].id)
  const product = project.products.find((item) => item.id === selectedProductId) ?? project.products[0]
  const [selectedChapterId, setSelectedChapterId] = useState(product.chapters[0].id)
  const chapter = product.chapters.find((item) => item.id === selectedChapterId) ?? product.chapters[0]
  const [selectedSessionId, setSelectedSessionId] = useState(chapter.sessions[0].id)
  const session = chapter.sessions.find((item) => item.id === selectedSessionId) ?? chapter.sessions[0]
  const [styleKey, setStyleKey] = useState<StyleKey>('cinematic')
  const activePanelMeta = panels.find((panel) => panel.id === activePanel) ?? panels[0]
  const [response, setResponse] = useState<DemoResponse>({
    title: 'Studio workspace ready',
    detail: 'Use the left navigation to move through project map, session preview, audit and export.',
    time: 'Just now',
  })

  const totals = useMemo(() => {
    const products = project.products.length
    const chapters = project.products.reduce((sum, item) => sum + item.chapters.length, 0)
    const sessions = project.products.reduce(
      (sum, item) => sum + item.chapters.reduce((chapterSum, current) => chapterSum + current.sessions.length, 0),
      0
    )
    const approved = project.products.reduce(
      (sum, item) =>
        sum + item.chapters.reduce((chapterSum, current) => chapterSum + current.sessions.filter((unit) => unit.status === 'Ready' || unit.status === 'Approved').length, 0),
      0
    )
    return { products, chapters, sessions, approved }
  }, [project])

  function action(title: string, detail: string) {
    const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    setResponse({ title, detail, time })
    if (typeof window === 'undefined' || window.innerWidth >= 768) {
      toast.success(title, { description: detail })
    }
  }

  function navigatePanel(panel: Panel) {
    setActivePanel(panel)
    setMobileMenuOpen(false)
  }

  function selectProduct(productId: string) {
    const nextProduct = project.products.find((item) => item.id === productId) ?? project.products[0]
    const nextChapter = nextProduct.chapters[0]
    const nextSession = nextChapter.sessions[0]
    setSelectedProductId(nextProduct.id)
    setSelectedChapterId(nextChapter.id)
    setSelectedSessionId(nextSession.id)
  }

  function selectChapter(chapterId: string) {
    const nextChapter = product.chapters.find((item) => item.id === chapterId) ?? product.chapters[0]
    setSelectedChapterId(nextChapter.id)
    setSelectedSessionId(nextChapter.sessions[0].id)
  }

  return (
    <main className="min-h-screen bg-background text-foreground">
      <DesktopSidebar
        activePanel={activePanel}
        project={project}
        response={response}
        onNavigate={navigatePanel}
      />

      <MobileDrawer
        open={mobileMenuOpen}
        activePanel={activePanel}
        project={project}
        response={response}
        onClose={() => setMobileMenuOpen(false)}
        onNavigate={navigatePanel}
      />

      <div className="min-h-screen lg:pl-[280px]">
        <TopBar
          activePanel={activePanelMeta}
          product={product}
          onMenu={() => setMobileMenuOpen(true)}
          onAddProduct={() => action('Product slot added', 'A new course slot was created inside the selected project.')}
          onGenerate={() => action('Storyboard generated', 'A first draft storyboard has been prepared for the selected session.')}
        />

        <section className="mx-auto w-full max-w-[1440px] space-y-5 p-3 sm:p-4 lg:p-6">
          <DemoResponsePanel response={response} />

          {activePanel === 'overview' && <OverviewPanel totals={totals} project={project} session={session} setActivePanel={navigatePanel} />}
          {activePanel === 'structure' && (
            <StructurePanel
              project={project}
              product={product}
              chapter={chapter}
              session={session}
              selectProduct={selectProduct}
              selectChapter={selectChapter}
              selectSession={setSelectedSessionId}
              action={action}
            />
          )}
          {activePanel === 'session' && (
            <SessionPanel
              session={session}
              styleKey={styleKey}
              setStyleKey={setStyleKey}
              action={action}
            />
          )}
          {activePanel === 'audit' && <AuditPanel session={session} action={action} />}
          {activePanel === 'exports' && <ExportPanel session={session} styleKey={styleKey} action={action} />}
          {activePanel === 'settings' && <SettingsPanel action={action} />}
        </section>
      </div>
    </main>
  )
}

function DesktopSidebar({ activePanel, project, response, onNavigate }: { activePanel: Panel; project: Project; response: DemoResponse; onNavigate: (panel: Panel) => void }) {
  return (
    <aside className="fixed inset-y-0 left-0 z-40 hidden w-[280px] flex-col border-r border-border bg-card/95 shadow-2xl shadow-black/20 backdrop-blur lg:flex">
      <SidebarContent activePanel={activePanel} project={project} response={response} onNavigate={onNavigate} />
    </aside>
  )
}

function MobileDrawer({ open, activePanel, project, response, onClose, onNavigate }: { open: boolean; activePanel: Panel; project: Project; response: DemoResponse; onClose: () => void; onNavigate: (panel: Panel) => void }) {
  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      <button type="button" aria-label="Close menu overlay" className="absolute inset-0 bg-black/65" onClick={onClose} />
      <aside className="relative flex h-full w-[min(86vw,320px)] flex-col border-r border-border bg-card shadow-2xl">
        <div className="absolute right-3 top-3">
          <Button size="icon" variant="ghost" onClick={onClose} aria-label="Close menu">
            <X className="h-5 w-5" />
          </Button>
        </div>
        <SidebarContent activePanel={activePanel} project={project} response={response} onNavigate={onNavigate} />
      </aside>
    </div>
  )
}

function SidebarContent({ activePanel, project, response, onNavigate }: { activePanel: Panel; project: Project; response: DemoResponse; onNavigate: (panel: Panel) => void }) {
  return (
    <div className="flex h-full min-h-0 flex-col">
      <Link to="/studio" className="flex h-16 shrink-0 items-center gap-3 border-b border-border px-4 no-underline">
        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-lg shadow-primary/20">
          <Sparkles className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="truncate text-xs text-muted-foreground">Konzult</p>
          <h1 className="truncate text-base font-semibold">Training Studio</h1>
        </div>
      </Link>

      <div className="border-b border-border p-4">
        <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">Workspace</p>
        <div className="mt-3 rounded-2xl border border-border bg-background/60 p-3">
          <p className="truncate font-medium">{project.title}</p>
          <p className="truncate text-sm text-muted-foreground">{project.client}</p>
          <Badge variant="warning" className="mt-3 w-fit">{project.status}</Badge>
        </div>
      </div>

      <nav className="min-h-0 flex-1 overflow-y-auto p-3" aria-label="Studio navigation">
        <div className="space-y-1">
          {panels.map((panel) => (
            <button
              key={panel.id}
              type="button"
              onClick={() => onNavigate(panel.id)}
              className={cn(
                'flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left transition',
                activePanel === panel.id
                  ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/15'
                  : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
              )}
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-background/20">{panel.icon}</span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium">{panel.label}</span>
                <span className={cn('block truncate text-xs', activePanel === panel.id ? 'text-primary-foreground/75' : 'text-muted-foreground')}>{panel.description}</span>
              </span>
            </button>
          ))}
        </div>
      </nav>

      <div className="shrink-0 border-t border-border p-4">
        <div className="rounded-2xl border border-primary/25 bg-primary/10 p-3 text-sm">
          <p className="font-medium text-primary">Last action</p>
          <p className="mt-1 line-clamp-2 text-muted-foreground">{response.title}</p>
        </div>
      </div>
    </div>
  )
}

function TopBar({ activePanel, product, onMenu, onAddProduct, onGenerate }: { activePanel: { label: string; description: string }; product: ProductCourse; onMenu: () => void; onAddProduct: () => void; onGenerate: () => void }) {
  return (
    <header className="sticky top-0 z-30 border-b border-border bg-background/90 backdrop-blur">
      <div className="mx-auto flex min-h-16 w-full max-w-[1440px] items-center gap-3 px-3 py-3 sm:px-4 lg:px-6">
        <Button size="icon" variant="ghost" className="shrink-0 lg:hidden" onClick={onMenu} aria-label="Open menu">
          <Menu className="h-5 w-5" />
        </Button>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>{activePanel.label}</span>
            <ChevronRight className="h-3 w-3" />
            <span className="truncate">{product.title}</span>
          </div>
          <h2 className="truncate text-lg font-semibold tracking-tight sm:text-xl">{activePanel.description}</h2>
        </div>

        <div className="hidden items-center gap-2 md:flex">
          <Button variant="outline" size="sm" className="gap-2" onClick={onAddProduct}>
            <Plus className="h-4 w-4" /> Add product
          </Button>
          <Button size="sm" className="gap-2" onClick={onGenerate}>
            <Wand2 className="h-4 w-4" /> Generate
          </Button>
        </div>

        <div className="flex items-center gap-1">
          <Button size="icon" variant="ghost" aria-label="Search" onClick={() => {
            if (typeof window === 'undefined' || window.innerWidth >= 768) toast.info('Search will connect when backend is added.')
          }}>
            <Search className="h-5 w-5" />
          </Button>
          <Button size="icon" variant="ghost" aria-label="Notifications" onClick={() => {
            if (typeof window === 'undefined' || window.innerWidth >= 768) toast.info('No new notifications.')
          }}>
            <Bell className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </header>
  )
}

function OverviewPanel({ totals, project, session, setActivePanel }: { totals: { products: number; chapters: number; sessions: number; approved: number }; project: Project; session: SessionItem; setActivePanel: (panel: Panel) => void }) {
  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Products / courses" value={totals.products.toString()} icon={<BookOpen className="h-5 w-5" />} />
        <MetricCard label="Chapters" value={totals.chapters.toString()} icon={<Layers className="h-5 w-5" />} />
        <MetricCard label="Sessions" value={totals.sessions.toString()} icon={<Film className="h-5 w-5" />} />
        <MetricCard label="Export-ready" value={totals.approved.toString()} icon={<CheckCircle2 className="h-5 w-5" />} />
      </div>

      <div className="grid gap-5 xl:grid-cols-[1.2fr_.8fr]">
        <CardShell>
          <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
            <div>
              <p className="text-sm text-muted-foreground">Project progress</p>
              <h3 className="mt-1 text-xl font-semibold">{project.title}</h3>
            </div>
            <Badge variant="warning" className="w-fit">{project.status}</Badge>
          </div>

          <div className="mt-5 grid gap-3 md:grid-cols-2">
            {workflow.map((step, index) => (
              <div key={step.label} className="rounded-2xl border border-border bg-background/60 p-4">
                <div className="flex items-start gap-3">
                  <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border', step.done ? 'border-primary bg-primary/15 text-primary' : 'border-border text-muted-foreground')}>
                    {step.done ? <CheckCircle2 className="h-4 w-4" /> : <Clock3 className="h-4 w-4" />}
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium">{index + 1}. {step.label}</p>
                    <p className="mt-1 text-sm leading-5 text-muted-foreground">{step.detail}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardShell>

        <CardShell>
          <p className="text-sm text-muted-foreground">Current session</p>
          <h3 className="mt-1 text-xl font-semibold">{session.title}</h3>
          <div className="mt-4 rounded-2xl border border-primary/20 bg-primary/10 p-4">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-muted-foreground">Ready for review</span>
              <span className="text-2xl font-semibold text-primary">{session.progress}%</span>
            </div>
            <div className="mt-3 h-2 rounded-full bg-secondary">
              <div className="h-2 rounded-full bg-primary" style={{ width: `${session.progress}%` }} />
            </div>
          </div>
          <div className="mt-5 grid gap-3">
            <Button className="justify-start gap-2" onClick={() => setActivePanel('structure')}>
              <Layers className="h-4 w-4" /> Open project map
            </Button>
            <Button variant="outline" className="justify-start gap-2" onClick={() => setActivePanel('session')}>
              <MonitorPlay className="h-4 w-4" /> Open session studio
            </Button>
            <Button variant="outline" className="justify-start gap-2" onClick={() => setActivePanel('audit')}>
              <ShieldCheck className="h-4 w-4" /> Run quality audit
            </Button>
          </div>
        </CardShell>
      </div>
    </div>
  )
}

function StructurePanel({ project, product, chapter, session, selectProduct, selectChapter, selectSession, action }: { project: Project; product: ProductCourse; chapter: Chapter; session: SessionItem; selectProduct: (id: string) => void; selectChapter: (id: string) => void; selectSession: (id: string) => void; action: (title: string, detail: string) => void }) {
  return (
    <div className="grid gap-5 xl:grid-cols-[380px_1fr]">
      <div className="space-y-4">
        <StructureBlock title="Project" subtitle={project.client} icon={<Building2 className="h-4 w-4" />} active>
          <p className="font-medium">{project.title}</p>
          <p className="mt-1 text-sm text-muted-foreground">One project can hold many training products/courses.</p>
        </StructureBlock>

        <StructureBlock title="Products / Courses" subtitle="Course library inside this project" icon={<BookOpen className="h-4 w-4" />}>
          {project.products.map((item) => (
            <ChoiceButton key={item.id} active={item.id === product.id} onClick={() => selectProduct(item.id)} title={item.title} subtitle={item.subtitle} />
          ))}
        </StructureBlock>

        <StructureBlock title="Chapters" subtitle="Sections inside the selected course" icon={<Layers className="h-4 w-4" />}>
          {product.chapters.map((item) => (
            <ChoiceButton key={item.id} active={item.id === chapter.id} onClick={() => selectChapter(item.id)} title={item.title} subtitle={item.status} />
          ))}
        </StructureBlock>
      </div>

      <CardShell>
        <div className="flex flex-col justify-between gap-3 md:flex-row md:items-center">
          <div>
            <p className="text-sm text-muted-foreground">Sessions inside selected chapter</p>
            <h3 className="text-xl font-semibold sm:text-2xl">{chapter.title}</h3>
          </div>
          <Button variant="outline" className="gap-2" onClick={() => action('Session added', 'A new lesson/video module was added under this chapter.')}>
            <Plus className="h-4 w-4" /> Add session
          </Button>
        </div>
        <div className="mt-5 grid gap-4 lg:grid-cols-2">
          {chapter.sessions.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => selectSession(item.id)}
              className={cn('rounded-2xl border p-4 text-left transition hover:border-primary/60 hover:bg-primary/5', item.id === session.id ? 'border-primary bg-primary/10' : 'border-border bg-background/70')}
            >
              <div className="mb-3 flex items-start justify-between gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/15 text-primary"><PlayCircle className="h-5 w-5" /></div>
                <StatusBadge status={item.status} />
              </div>
              <h4 className="font-semibold">{item.title}</h4>
              <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{item.source}</p>
              <div className="mt-4 h-2 rounded-full bg-secondary"><div className="h-2 rounded-full bg-primary" style={{ width: `${item.progress}%` }} /></div>
              <p className="mt-2 text-xs text-muted-foreground">{item.progress}% ready · {item.duration}</p>
            </button>
          ))}
        </div>
      </CardShell>
    </div>
  )
}

function SessionPanel({ session, styleKey, setStyleKey, action }: { session: SessionItem; styleKey: StyleKey; setStyleKey: (key: StyleKey) => void; action: (title: string, detail: string) => void }) {
  const hasRenderedVideo = session.id === 'session-clause-4-1'
  const activeStyle = STYLE_OUTPUTS[styleKey]

  function switchStyle(next: StyleKey) {
    setStyleKey(next)
    action('Style switched', `${STYLE_OUTPUTS[next].label} cut is now playing in the preview.`)
  }

  return (
    <div className="grid gap-5 xl:grid-cols-[1fr_400px]">
      <CardShell>
        <div className="flex flex-col justify-between gap-3 md:flex-row md:items-start">
          <div>
            <p className="text-sm text-muted-foreground">Selected session</p>
            <h3 className="text-xl font-semibold sm:text-2xl">{session.title}</h3>
          </div>
          <StatusBadge status={session.status} />
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-3">
          <Button className="gap-2" onClick={() => action('Preview refreshed', 'The HTML preview was regenerated for this session.')}><Eye className="h-4 w-4" /> Preview</Button>
          <Button variant="outline" className="gap-2" onClick={() => action('Comment added', 'A review note was attached to this scene.')}><MessageSquare className="h-4 w-4" /> Comment</Button>
          <Button variant="outline" className="gap-2" onClick={() => action('Source uploaded', 'Training material was attached to the selected session.')}><UploadCloud className="h-4 w-4" /> Upload</Button>
        </div>

        {hasRenderedVideo ? (
          <div className="mt-5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">Production style</span>
              <div className="flex flex-wrap gap-2" role="tablist" aria-label="Production style">
                {(Object.keys(STYLE_OUTPUTS) as StyleKey[]).map((key) => {
                  const isActive = key === styleKey
                  return (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => switchStyle(key)}
                      className={cn(
                        'rounded-full border px-3 py-1.5 text-xs font-medium transition sm:text-sm',
                        isActive
                          ? 'border-primary bg-primary text-primary-foreground shadow-lg shadow-primary/20'
                          : 'border-border bg-background/60 text-muted-foreground hover:border-primary/50 hover:text-foreground'
                      )}
                    >
                      {STYLE_OUTPUTS[key].label}
                    </button>
                  )
                })}
              </div>
            </div>
            <div className="mt-3 overflow-hidden rounded-3xl border border-primary/20 bg-black shadow-2xl shadow-primary/5">
              <video
                key={styleKey}
                controls
                preload="metadata"
                src={activeStyle.videoUrl}
                className="aspect-video w-full bg-black"
              />
            </div>
            <p className="mt-2 text-xs text-muted-foreground sm:text-sm">{activeStyle.subtitle}</p>
          </div>
        ) : (
          <div className="mt-5 min-h-[430px] overflow-hidden rounded-3xl border border-primary/20 bg-background p-3 shadow-2xl shadow-primary/5 sm:aspect-video sm:min-h-[360px] sm:p-5">
            <div className="flex h-full min-h-[404px] flex-col justify-between gap-4 rounded-2xl border border-border bg-gradient-to-br from-primary/10 via-background to-accent/10 p-4 sm:min-h-0 sm:p-5">
              <div className="flex items-center justify-between text-xs text-muted-foreground sm:text-sm"><span>HTML preview</span><span>{session.duration}</span></div>
              <div>
                <p className="text-xs uppercase tracking-[0.22em] text-primary sm:text-sm">Training scene</p>
                <h4 className="mt-3 max-w-xl text-xl font-semibold tracking-tight sm:text-3xl">Awaiting first render</h4>
                <p className="mt-3 max-w-lg text-sm leading-6 text-muted-foreground sm:text-base">Storyboard and script are queued. Final video will appear here once the render passes audit.</p>
              </div>
              <div className="grid gap-2 text-xs sm:grid-cols-3 sm:text-sm">
                {session.storyboard.slice(0, 3).map((item) => <div key={item} className="rounded-xl border border-border bg-card/70 p-3">{item}</div>)}
              </div>
            </div>
          </div>
        )}
      </CardShell>

      <div className="space-y-5">
        <InfoCard icon={<FileText className="h-5 w-5" />} title="Source material" text={session.source} />
        <InfoCard icon={<Wand2 className="h-5 w-5" />} title="AI script direction" text={session.script} />
        <CardShell>
          <h4 className="font-semibold">Storyboard beats</h4>
          <div className="mt-4 space-y-3">
            {session.storyboard.map((item, index) => (
              <div key={item} className="flex items-center gap-3 rounded-xl border border-border bg-background/60 p-3">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs text-primary">{index + 1}</div>
                <p className="text-sm">{item}</p>
              </div>
            ))}
          </div>
        </CardShell>
      </div>
    </div>
  )
}

function AuditPanel({ session, action }: { session: SessionItem; action: (title: string, detail: string) => void }) {
  const checks = [
    { label: 'Text readable on phone and laptop', done: true },
    { label: 'Scene spacing and safe area checked', done: true },
    { label: 'Fast neon lines reduced for no-flicker profile', done: false },
    { label: 'Ash approval captured before final MP4', done: false },
  ]
  return (
    <div className="grid gap-5 xl:grid-cols-[1fr_380px]">
      <CardShell>
        <div className="flex flex-col justify-between gap-3 md:flex-row md:items-center">
          <div>
            <p className="text-sm text-muted-foreground">Quality gate</p>
            <h3 className="text-xl font-semibold sm:text-2xl">{session.title}</h3>
          </div>
          <Button className="gap-2" onClick={() => action('Audit queued', 'The session is checked for readability, layout, flicker risk and approval status.')}><ShieldCheck className="h-4 w-4" /> Run audit</Button>
        </div>
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {checks.map((check) => (
            <div key={check.label} className="rounded-2xl border border-border bg-background/70 p-4">
              <div className="mb-3 flex items-center gap-3">
                {check.done ? <CheckCircle2 className="h-5 w-5 text-primary" /> : <Clock3 className="h-5 w-5 text-muted-foreground" />}
                <p className="font-medium">{check.label}</p>
              </div>
              <Badge variant={check.done ? 'success' : 'warning'}>{check.done ? 'Passed' : 'Needs review'}</Badge>
            </div>
          ))}
        </div>
      </CardShell>
      <CardShell>
        <h4 className="font-semibold">Current audit notes</h4>
        <div className="mt-4 space-y-3">
          {session.audit.map((item) => (
            <div key={item} className="flex gap-3 rounded-xl border border-border bg-background/60 p-3 text-sm">
              <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </CardShell>
    </div>
  )
}

function ExportPanel({ session, styleKey, action }: { session: SessionItem; styleKey: StyleKey; action: (title: string, detail: string) => void }) {
  const activeStyle = STYLE_OUTPUTS[styleKey]
  const reviewUrl = STYLE_OUTPUTS.corporate.reviewPageUrl

  function openReviewLink() {
    if (reviewUrl && typeof window !== 'undefined') window.open(reviewUrl, '_blank', 'noopener')
    action('Review link opened', 'Branded review page for the corporate cut is now open in a new tab.')
  }

  function openTrainingMp4() {
    if (typeof window !== 'undefined') window.open(activeStyle.videoUrl, '_blank', 'noopener')
    action('MP4 export ready', `${session.title} · ${activeStyle.label} cut opened in a new tab for download.`)
  }

  return (
    <div className="grid gap-5 lg:grid-cols-3">
      <ExportCard title="Review link" text="Branded review page for the corporate cut. Cinematic review page is on the roadmap." button="Open review link" icon={<Eye className="h-5 w-5" />} onClick={openReviewLink} />
      <ExportCard title="Training video" text={`Final MP4 export for the currently selected style (${activeStyle.label}).`} button="Open MP4" icon={<Film className="h-5 w-5" />} onClick={openTrainingMp4} />
      <ExportCard title="LMS package" text="Future package for LMS/social snippets/certificates." button="Plan LMS export" icon={<Rocket className="h-5 w-5" />} onClick={() => action('LMS export planned', 'Future export slots were added without changing the current frontend structure.')} />
    </div>
  )
}

function SettingsPanel({ action }: { action: (title: string, detail: string) => void }) {
  return (
    <div className="grid gap-5 xl:grid-cols-2">
      <CardShell>
        <h3 className="text-xl font-semibold sm:text-2xl">Studio settings</h3>
        <p className="mt-2 text-muted-foreground">These controls reserve space for backend, database and multi-tenant setup later.</p>
        <div className="mt-5 space-y-3">
          {['Workspace roles', 'Brand templates', 'Voice presets', 'Approval policy'].map((item) => <InfoRow key={item} label={item} value="Planned" />)}
        </div>
        <Button className="mt-5 gap-2" onClick={() => action('Settings saved', 'Frontend settings were saved in the response panel.')}><CheckCircle2 className="h-4 w-4" /> Save settings</Button>
      </CardShell>
      <CardShell>
        <h3 className="text-xl font-semibold sm:text-2xl">Application structure</h3>
        <p className="mt-2 text-muted-foreground">The app is built around the correct content library hierarchy.</p>
        <div className="mt-5 rounded-2xl border border-primary/20 bg-primary/10 p-4 text-sm text-primary">Workspace → Project → Product/Course → Chapter → Session</div>
      </CardShell>
    </div>
  )
}

function DemoResponsePanel({ response }: { response: DemoResponse }) {
  return (
    <div className="rounded-3xl border border-primary/25 bg-primary/10 p-4">
      <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-primary">Action response</p>
          <h3 className="mt-1 truncate font-semibold">{response.title}</h3>
          <p className="text-sm text-muted-foreground">{response.detail}</p>
        </div>
        <Badge variant="outline" className="w-fit shrink-0 border-primary/30 text-primary">{response.time}</Badge>
      </div>
    </div>
  )
}

function MetricCard({ label, value, icon }: { label: string; value: string; icon: ReactNode }) {
  return <div className="rounded-3xl border border-border bg-card p-5"><div className="mb-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/15 text-primary">{icon}</div><p className="text-sm text-muted-foreground">{label}</p><p className="mt-1 text-3xl font-semibold">{value}</p></div>
}

function StructureBlock({ title, subtitle, icon, children, active = false }: { title: string; subtitle: string; icon: ReactNode; children: ReactNode; active?: boolean }) {
  return <div className={cn('rounded-3xl border p-4', active ? 'border-primary/40 bg-primary/10' : 'border-border bg-card')}><div className="mb-3 flex items-start gap-3"><div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">{icon}</div><div><p className="font-semibold">{title}</p><p className="text-sm text-muted-foreground">{subtitle}</p></div></div><div className="space-y-2">{children}</div></div>
}

function ChoiceButton({ active, onClick, title, subtitle }: { active: boolean; onClick: () => void; title: string; subtitle: string }) {
  return <button type="button" onClick={onClick} className={cn('w-full rounded-xl border p-3 text-left transition', active ? 'border-primary bg-primary/10' : 'border-border bg-background/60 hover:border-primary/50')}><p className="text-sm font-medium">{title}</p><p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{subtitle}</p></button>
}

function StatusBadge({ status }: { status: Status }) {
  const variant = status === 'Ready' || status === 'Approved' ? 'success' : status === 'Needs work' ? 'destructive' : status === 'In review' ? 'warning' : 'secondary'
  return <Badge variant={variant}>{status}</Badge>
}

function InfoCard({ icon, title, text }: { icon: ReactNode; title: string; text: string }) {
  return <CardShell><div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-primary/15 text-primary">{icon}</div><h4 className="font-semibold">{title}</h4><p className="mt-2 text-sm leading-6 text-muted-foreground">{text}</p></CardShell>
}

function ExportCard({ title, text, button, icon, onClick }: { title: string; text: string; button: string; icon: ReactNode; onClick: () => void }) {
  return <CardShell><div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/15 text-primary">{icon}</div><h3 className="text-xl font-semibold">{title}</h3><p className="mt-3 min-h-16 text-sm leading-6 text-muted-foreground">{text}</p><Button className="mt-5 w-full gap-2" onClick={onClick}>{button}<Zap className="h-4 w-4" /></Button></CardShell>
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-background/60 p-3 text-sm"><span>{label}</span><Badge variant="secondary">{value}</Badge></div>
}

function CardShell({ children }: { children: ReactNode }) {
  return <div className="rounded-3xl border border-border bg-card p-4 shadow-xl shadow-black/10 sm:p-5">{children}</div>
}

export const routeTree = rootRoute.addChildren([indexRoute, studioRoute])
